import clsx from 'clsx';
import type { LiveCallStats } from '../../hooks/useCallStats';

interface Props {
  stats: LiveCallStats | null;
  compact?: boolean;
}

/**
 * Three-bar signal indicator. Colour + active-bar count reflect quality.
 * Renders a greyed-out placeholder while no samples have arrived yet.
 */
export function NetworkQualityIndicator({ stats, compact = false }: Props) {
  const quality = stats?.quality ?? null;
  const activeBars = quality === 'good' ? 3 : quality === 'fair' ? 2 : quality === 'poor' ? 1 : 0;

  const colour =
    quality === 'good'
      ? 'bg-green-500'
      : quality === 'fair'
        ? 'bg-amber-500'
        : quality === 'poor'
          ? 'bg-red-500'
          : 'bg-slate-300';

  const title = stats
    ? `Verbinding ${qualityLabel(quality)} — ${stats.packetLossPct.toFixed(1)}% pakketverlies, jitter ${stats.jitterMs.toFixed(0)}ms, rtt ${stats.rttMs.toFixed(0)}ms`
    : 'Verbindingskwaliteit laden…';

  const ariaLabel = stats
    ? `Verbindingskwaliteit ${qualityLabel(quality)}, ${stats.packetLossPct.toFixed(1)}% pakketverlies`
    : 'Verbindingskwaliteit laden';

  return (
    <div
      className={clsx('inline-flex items-end gap-0.5', compact ? 'h-3' : 'h-4')}
      title={title}
      aria-label={ariaLabel}
      role="img"
    >
      {[1, 2, 3].map((bar) => (
        <span
          key={bar}
          className={clsx(
            'w-1 rounded-sm',
            bar <= activeBars ? colour : 'bg-slate-300',
            bar === 1 && (compact ? 'h-1.5' : 'h-2'),
            bar === 2 && (compact ? 'h-2.5' : 'h-3'),
            bar === 3 && (compact ? 'h-3' : 'h-4'),
          )}
        />
      ))}
    </div>
  );
}

function qualityLabel(q: LiveCallStats['quality'] | null): string {
  if (q === 'good') return 'goed';
  if (q === 'fair') return 'matig';
  if (q === 'poor') return 'slecht';
  return 'onbekend';
}
